import { useState } from "react";
import { Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThreeDRendersPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...form,
        service_type: "3d_renders",
      });
      toast.success("Enquiry submitted! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <main data-testid="3d-renders-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-4 font-semibold">
                Bespoke Service
              </p>
              <h1
                data-testid="renders-headline"
                className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light text-[#0D2D54] leading-[1.1] tracking-tight mb-6"
              >
                Photorealistic 3D renders for property developers
              </h1>
              <p className="text-base text-[#6B7280] leading-relaxed mb-8 max-w-md">
                Bring your off-plan developments to life with stunning architectural visualisations that sell properties before they're built.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Exterior and interior renders",
                  "Perfect for off-plan marketing",
                  "Premium quality at competitive prices",
                  "Fast turnaround on all projects",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-[#6B7280]">
                    <Check size={16} className="text-[#758C7B] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/mlpg0mca_stockton-roadexteriorfinal.jpg"
                alt="3D Property Render"
                className="w-full h-[400px] lg:h-[500px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-20 lg:py-28">
        <div className="max-w-2xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Get Started
            </p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0D2D54]">
              Request a Quote
            </h2>
            <p className="text-sm text-[#6B7280] mt-3">
              Tell us about your project and we'll provide a tailored quote within 24 hours.
            </p>
            <p className="text-sm text-[#6B7280] mt-1">
              Or email us directly at{" "}
              <a href="mailto:rachel@thesecretseller.co.uk" className="text-[#0D2D54] font-medium hover:underline">
                rachel@thesecretseller.co.uk
              </a>
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="renders-enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Name *</label>
                <input
                  type="text"
                  data-testid="renders-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border-b border-[#E8E8E4] bg-transparent py-3 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Email *</label>
                <input
                  type="email"
                  data-testid="renders-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border-b border-[#E8E8E4] bg-transparent py-3 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Phone (optional)</label>
              <input
                type="tel"
                data-testid="renders-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full border-b border-[#E8E8E4] bg-transparent py-3 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
                placeholder="Your phone number"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Project Details *</label>
              <textarea
                data-testid="renders-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                className="w-full border border-[#E8E8E4] bg-transparent p-3 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors resize-none"
                placeholder="Tell us about your project..."
              />
            </div>
            <button
              type="submit"
              data-testid="renders-submit-btn"
              disabled={submitting}
              className="w-full bg-[#0D2D54] text-white py-4 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Request a Quote"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
